// DashboardHome — Phase 5 Wave 2.5 (v2-cp migration).
//
// Migrated off the legacy cookie-based `@intelliauth/api-client` to the
// new JWT-Bearer `@intelliauth/api-client-v2-cp`. This page consumes
// just `GET /api/v1/organizations/{id}/tenants` for the at-a-glance
// list + total-tenants stat. The cross-tenant user-count summary that
// used to fan out via the legacy `dataPlane(...)` proxy is removed —
// it depended on a v1-only DP proxy endpoint and is replaced by a
// "Coming soon" placeholder; the proper home is BE-21 cross-tenant
// users (Wave 6) once that ships.

import type { components } from '@intelliauth/api-client-v2-cp/types'
import { Button, buttonVariants, Card, StatCard } from '@intelliauth/ui'
import {
  Activity,
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  Clock,
  ExternalLink,
  Globe,
  Headphones,
  Loader2,
  Mail,
  Plus,
  Server,
  Users,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import CreateTenantDialog from '@/components/CreateTenantDialog'
import { useAuth } from '@/contexts/AuthContext'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'
import { tenantBaseUrl } from '@/lib/domain'

type Tenant = components['schemas']['Tenant']

// Accept any string for forward compatibility with backend states
// (decommissioned, decommissioning, provisioning, failed) not yet in
// the OpenAPI Tenant.status enum. Falls back to "Unknown".
const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
  active: { label: 'Active', bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
  pending: { label: 'Provisioning', bg: 'bg-blue-500/10', text: 'text-blue-400' },
  provisioning: { label: 'Provisioning', bg: 'bg-blue-500/10', text: 'text-blue-400' },
  suspended: { label: 'Suspended', bg: 'bg-orange-500/10', text: 'text-orange-400' },
  failed: { label: 'Failed', bg: 'bg-red-500/10', text: 'text-red-400' },
  decommissioning: { label: 'Decommissioning', bg: 'bg-orange-500/10', text: 'text-orange-400' },
  decommissioned: { label: 'Decommissioned', bg: 'bg-gray-500/10', text: 'text-gray-400' },
  deleted: { label: 'Archived', bg: 'bg-gray-500/10', text: 'text-gray-400' },
}

const UNKNOWN_STATUS_HOME = { label: 'Unknown', bg: 'bg-gray-500/10', text: 'text-gray-400' }

export default function DashboardHome() {
  const { user, currentOrg } = useAuth()
  const navigate = useNavigate()
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)

  const welcomeName = user?.email?.split('@')[0] ?? 'there'

  const fetchData = useCallback(async () => {
    if (!currentOrg) return
    try {
      const env = await cpClient().get('/api/v1/organizations/{id}/tenants', {
        path: { id: currentOrg.id },
      })
      const data = (env as { data: Tenant[] }).data ?? []
      setTenants(data)
    } catch (err) {
      const msg =
        err instanceof CpV2ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load tenants'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }, [currentOrg])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-foreground tracking-tight">
          Welcome back, {welcomeName}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Here's what's happening with your organization today.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowCreateDialog(true)}
          leftIcon={Plus}
        >
          Create tenant
        </Button>
        <Link
          to="/dashboard/members"
          className={buttonVariants({ variant: 'outline', size: 'sm' })}
        >
          <Users className="h-4 w-4" />
          Manage members
        </Link>
        <a href="#" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
          <BookOpen className="h-4 w-4" />
          View docs
        </a>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Tenants"
          value={String(tenants.length)}
          icon={Server}
          loading={loading}
        />
        <StatCard label="Total Users" value="—" icon={Users} loading={loading}>
          <p className="text-caption text-ia-text-tertiary">Coming soon</p>
        </StatCard>
        <StatCard label="Monthly Authentications" value="—" icon={Activity} loading={loading}>
          <p className="text-caption text-ia-text-tertiary">Coming soon</p>
        </StatCard>
        <StatCard label="Active Sessions" value="—" icon={Clock} loading={loading}>
          <p className="text-caption text-ia-text-tertiary">Coming soon</p>
        </StatCard>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Your tenants</h2>
          <Link
            to="/dashboard/tenants"
            className="text-sm text-primary hover:text-primary/80 font-medium"
          >
            View all
          </Link>
        </div>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="p-5 animate-pulse" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="h-5 w-32 bg-muted rounded mb-2" />
                <div className="h-3 w-48 bg-muted rounded mb-4" />
                <div className="h-3 w-24 bg-muted rounded" />
              </Card>
            ))}
          </div>
        ) : tenants.length === 0 ? (
          <Card className="p-8 text-center">
            <Server className="h-8 w-8 text-muted-foreground/70 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-4">
              No tenants yet. Create your first tenant to get started.
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreateDialog(true)}
              leftIcon={Plus}
            >
              Create tenant
            </Button>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tenants.slice(0, 6).map((tenant, i) => (
              <TenantCard
                key={tenant.id}
                tenant={tenant}
                orgSlug={currentOrg?.slug ?? ''}
                index={i}
              />
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-foreground mb-4">Recent activity</h2>
          <Card className="p-6 flex flex-col items-center justify-center text-center">
            <Clock className="h-8 w-8 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">Activity feed coming soon</p>
            <p className="text-xs text-muted-foreground/60 mt-1">
              Audit logs and real-time activity will appear here.
            </p>
          </Card>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">Quick links</h2>
          <div className="space-y-2">
            <QuickLink icon={<BookOpen className="h-4 w-4" />} label="Documentation" href="#" />
            <QuickLink icon={<Activity className="h-4 w-4" />} label="API Status" href="#" />
            <QuickLink icon={<Headphones className="h-4 w-4" />} label="Support" href="#" />
          </div>
        </div>
      </div>

      {currentOrg && (
        <CreateTenantDialog
          open={showCreateDialog}
          orgId={currentOrg.id}
          onOpenChange={(open) => {
            if (!open) setShowCreateDialog(false)
          }}
          onSuccess={() => {
            setShowCreateDialog(false)
            navigate('/dashboard/tenants')
          }}
        />
      )}
    </div>
  )
}

function TenantCard({
  tenant,
  orgSlug,
  index = 0,
}: {
  tenant: Tenant
  orgSlug: string
  index?: number
}) {
  const status = statusConfig[tenant.status] ?? UNKNOWN_STATUS_HOME
  const adminUrl = tenantBaseUrl(tenant.slug, orgSlug, { path: '/admin' })

  return (
    <a
      href={tenant.status === 'active' ? adminUrl : undefined}
      target="_blank"
      rel="noopener noreferrer"
      className="rounded-xl bg-card text-card-foreground shadow-sm p-5 hover:bg-foreground/[0.04] transition-colors animate-fade-in block"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground truncate">{tenant.name}</h3>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${status.bg} ${status.text}`}
            >
              {tenant.status === 'pending' && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
              {tenant.status === 'active' && <CheckCircle2 className="h-2.5 w-2.5" />}
              {status.label}
            </span>
          </div>
          <p className="text-xs text-muted-foreground/70 mt-0.5 font-mono truncate">
            {tenant.slug}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-3 flex-wrap">
        <span className="flex items-center gap-1">
          <Globe className="h-3 w-3" />
          {tenant.region}
        </span>
        {/* Admin recipients — display mirror, same source + semantics as
            TenantsPage.TenantCard. Truncated more aggressively here
            because the homepage card is half the width. This is read from
            the cp-side mirror rather than the per-tenant DB. */}
        {tenant.admin_emails && tenant.admin_emails.length > 0 && (
          <span
            className="flex items-center gap-1 min-w-0"
            title="Initial admin recipients set at provisioning. In-tenant changes do not update this list."
          >
            <Mail className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">
              {tenant.admin_emails[0]}
              {tenant.admin_emails.length > 1 && (
                <span className="text-muted-foreground/60">
                  {' '}
                  +{tenant.admin_emails.length - 1}
                </span>
              )}
            </span>
          </span>
        )}
      </div>

      {tenant.status === 'active' && (
        <div className="mt-3 flex items-center gap-1 text-sm text-primary font-medium">
          Open Tenant Admin
          <ExternalLink className="h-3.5 w-3.5" />
        </div>
      )}
    </a>
  )
}

function QuickLink({ icon, label, href }: { icon: React.ReactNode; label: string; href: string }) {
  return (
    <a
      href={href}
      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-card text-card-foreground shadow-sm hover:bg-foreground/[0.04] transition-colors group"
    >
      <span className="text-muted-foreground group-hover:text-primary">{icon}</span>
      <span className="text-sm text-foreground group-hover:text-primary font-medium">{label}</span>
      <ArrowUpRight className="h-3.5 w-3.5 ml-auto text-muted-foreground/70 group-hover:text-primary" />
    </a>
  )
}
